import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import ConfigTable, { ConfigTableEmptyCell } from '~components/Common/ConfigTable';
import { CatalogedCompanySettingKey } from '~pages/Settings/Configuration/companySettingsCatalog';

import CompanySettingToggle from './CompanySettingToggle';

interface DispatchTableColumn {
  jobsTableSettingKey: CatalogedCompanySettingKey | null;
  labelKey: string;
  ordersTableSettingKey: CatalogedCompanySettingKey | null;
}

const dispatchTableColumns: DispatchTableColumn[] = [
  {
    jobsTableSettingKey: null,
    labelKey: 'form_fields.equipment_type',
    ordersTableSettingKey: 'hide_dispatch_orders_equipment_type_column',
  },
  {
    jobsTableSettingKey: 'show_dispatch_jobs_materials_column',
    labelKey: 'form_fields.materials',
    ordersTableSettingKey: 'hide_dispatch_orders_materials_column',
  },
  {
    jobsTableSettingKey: 'show_dispatch_jobs_pickup_dropoff_column',
    labelKey: 'dispatch.dispatch_v2.pick_up_and_drop_off',
    ordersTableSettingKey: 'hide_dispatch_orders_pickup_dropoff_column',
  },
  {
    jobsTableSettingKey: null,
    labelKey: 'form_fields.start_time',
    ordersTableSettingKey: 'hide_dispatch_orders_start_time_column',
  },
  {
    jobsTableSettingKey: 'show_dispatch_jobs_truck_and_trailer_column',
    labelKey: 'dispatch.dispatch_v2.truck_and_trailer',
    ordersTableSettingKey: null,
  },
];

interface DispatchTableColumnSettingsProps {
  settingKeys: CatalogedCompanySettingKey[];
}

export default function DispatchTableColumnSettings({
  settingKeys,
}: DispatchTableColumnSettingsProps) {
  const isVisibleSettingKey = (
    settingKey: CatalogedCompanySettingKey | null,
  ): settingKey is CatalogedCompanySettingKey =>
    settingKey !== null && settingKeys.includes(settingKey);

  const visibleColumns = dispatchTableColumns.filter(
    (column) =>
      isVisibleSettingKey(column.ordersTableSettingKey) ||
      isVisibleSettingKey(column.jobsTableSettingKey),
  );

  if (visibleColumns.length === 0) {
    return null;
  }

  const renderSettingCell = (settingKey: CatalogedCompanySettingKey | null) => {
    if (!isVisibleSettingKey(settingKey)) {
      return <ConfigTableEmptyCell />;
    }

    return (
      <TableCell align="center">
        <CompanySettingToggle hideLabel settingKey={settingKey} />
      </TableCell>
    );
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {t('administration.configuration.settings.display.dispatch_table_columns.title')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
        {t(
          'administration.configuration.settings.display.dispatch_table_columns.description',
        )}
      </Typography>
      <ConfigTable
        columnLabels={[
          t(
            'administration.configuration.settings.display.dispatch_table_columns.column',
          ),
          t(
            'administration.configuration.settings.display.dispatch_table_columns.orders_table',
          ),
          t(
            'administration.configuration.settings.display.dispatch_table_columns.jobs_table',
          ),
        ]}
      >
        {visibleColumns.map((column) => (
          <TableRow key={column.labelKey}>
            <TableCell component="th" scope="row">
              {t(column.labelKey)}
            </TableCell>
            {renderSettingCell(column.ordersTableSettingKey)}
            {renderSettingCell(column.jobsTableSettingKey)}
          </TableRow>
        ))}
      </ConfigTable>
    </Box>
  );
}
